import { create } from "zustand";
import type {
  AllowedRoute,
  MeFeatureFlags,
  MePermissions,
  MeResponse,
  MeUser,
  NavItem,
} from "@/api/types";
import {
  readPersistedCredentials,
  persistCredentials,
  clearPersistedCredentials,
  describeStorageEnv,
  type PersistedCredentials,
} from "@/lib/credentialsStorage";

export type AuthCredentials = PersistedCredentials;

interface AuthState {
  credentials: AuthCredentials | null;
  user: MeUser | null;
  permissions: MePermissions | null;
  visibleNavigation: NavItem[];
  /** NAV-1 — backend-owned left-nav visibility contract (renders the Sidebar). */
  allowedRoutes: AllowedRoute[];
  featureFlags: MeFeatureFlags | null;
  setCredentials: (creds: AuthCredentials) => void;
  setSession: (me: MeResponse) => void;
  reset: () => void;
}

// One-shot diagnostic at module load — surfaces Brave/Safari storage state
// without forcing the user to manually probe.
console.warn("[KORMAX-AUTH]", "[storage env]", describeStorageEnv());

export const useAuthStore = create<AuthState>((set) => ({
  credentials: readPersistedCredentials(),
  user: null,
  permissions: null,
  visibleNavigation: [],
  allowedRoutes: [],
  featureFlags: null,
  setCredentials: (creds) => {
    persistCredentials(creds);
    set({ credentials: creds });
  },
  setSession: (me) =>
    set({
      user: me.user,
      permissions: me.permissions,
      visibleNavigation: me.visibleNavigation,
      allowedRoutes: me.allowedRoutes ?? [],
      featureFlags: me.featureFlags,
    }),
  reset: () => {
    clearPersistedCredentials();
    set({
      credentials: null,
      user: null,
      permissions: null,
      visibleNavigation: [],
      allowedRoutes: [],
      featureFlags: null,
    });
  },
}));

/**
 * Module-level credentials accessor — used by api/client.ts so every
 * request can read the in-memory authoritative copy without re-subscribing.
 */
export function getAuthCredentials(): AuthCredentials | null {
  return useAuthStore.getState().credentials;
}
